import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, UserPassword } from '../../../../api/model';
import { usePostUsersPassword } from '../../../../api/users/users';
import { useDefaultOptions } from '../../../../hooks/api';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

type Props = {
  UserId: number | undefined;
  open: boolean;
  onClose: () => void;
};

function UserPasswordModal({ UserId, open, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<UserPassword>({
    defaultValues: {
      user_id: UserId,
      password_now: undefined,
      password_new: undefined,
      password_con: undefined,
    },
  });

  const { mutateAsync, error: userPasswordError } = usePostUsersPassword<AxiosError<ErrorInfo>, unknown>(options); // パスワード変更API
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayUserPasswordError, setDisplayUserPasswordError] = useState(false); // パスワード変更エラー
  const [displayUserPasswordOk, setDisplayUserPasswordOk] = useState(false); // パスワード変更OK
  const [displayConfirmUserPassword, setDisplayConfirmUserPassword] = useState(false); // パスワード変更確認ダイアログ

  useEffect(() => {
    setDisplayUserPasswordError(userPasswordError != null);
    setIsSubmitting(false);
  }, [userPasswordError]);

  const onSubmit = useCallback(() => {
    void handleSubmit(async (data: FieldValues) => {
      setIsSubmitting(true);
      await mutateAsync(
        {
          data,
        },
        {
          onSuccess: () => {
            setDisplayUserPasswordOk(true);
          },
        }
      );
    })();
  }, [setIsSubmitting, setDisplayUserPasswordOk, handleSubmit, mutateAsync]);

  return (
    <Modal active={open} additionalClassName="popupfr600" displayCloseButton onClose={onClose}>
      <form onSubmit={onSubmit}>
        <div className="popupfr popupfr644">
          <div className="spm">
            <h4>{t('パスワード変更')}</h4>
            <div className="labip1">
              <div>
                <label htmlFor="password_now">{t('元のパスワード')}</label>
                <div>
                  <input type="hidden" id="user_id" {...register('user_id')} />
                  <input
                    type="password"
                    id="password_now"
                    className={errors.password_now?.type === 'required' ? 'error' : ''}
                    {...register('password_now', { required: true })}
                  />
                  {errors.password_now?.type === 'required' && <p className="errormessage show">{t('必須項目です')}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="password_new">{t('新規パスワード')}</label>
                <div>
                  <input
                    type="password"
                    id="password_new"
                    className={errors.password_new?.type === 'required' ? 'error' : ''}
                    {...register('password_new', { required: true })}
                  />
                  {errors.password_new?.type === 'required' && <p className="errormessage show">{t('必須項目です')}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="password_con">{t('新規パスワード（確認用）')}</label>
                <div>
                  <input
                    type="password"
                    id="password_con"
                    className={errors.password_con?.type === 'required' ? 'error' : ''}
                    {...register('password_con', {
                      required: true,
                    })}
                  />
                  {errors.password_con?.type === 'required' && <p className="errormessage show">{t('必須項目です')}</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="btnfr btnfr2 mb0">
            <div>
              <span className="btn2 btnbg-g mr10">
                <span className="modal-close2" onClick={onClose}>
                  {t('キャンセル').toString()}
                </span>
              </span>
              <span>
                <input
                  type="button"
                  value={t('変更').toString()}
                  className="btn1 lsp1"
                  onClick={() => setDisplayConfirmUserPassword(true)}
                />
              </span>
            </div>
          </div>
        </div>
      </form>

      {/* ユーザー引継ぎ確認 */}
      {displayConfirmUserPassword && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmUserPassword(false)}
          onClickOk={() => {
            setDisplayConfirmUserPassword(false);
            onSubmit();
          }}
        >
          {t('パスワードを変更しますか？')}
        </ConfirmModal>
      )}
      {isSubmitting && <SpinnerModal />}
      {displayUserPasswordError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayUserPasswordError(false);
          }}
          error={userPasswordError}
        />
      )}
      {displayUserPasswordOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayConfirmUserPassword(false);
            onClose();
          }}
        >
          <div>{t('パスワードを変更しました。')}</div>
        </OkModal>
      )}
    </Modal>
  );
}
export { UserPasswordModal };
